import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, FileText, Package } from "lucide-react";

// Where each card sends the user. If your document upload page is mounted on a
// different path than "/verification", change DOCUMENT_UPLOAD_ROUTE only.
const DOCUMENT_UPLOAD_ROUTE = "/verification";
const ARTIFACT_UPLOAD_ROUTE = "/artifact-verification";

const TYPES = [
  {
    id: "document",
    title: "Verify Document",
    icon: FileText,
    description:
      "Check a signed PDF for authenticity: signature, certificate, timestamp, blockchain anchor and AI tamper detection.",
    bullets: [
      "PDF documents, contracts, reports, certificates",
      "Includes AI Tamper Detection (OCR, layout, metadata)",
    ],
    route: DOCUMENT_UPLOAD_ROUTE,
  },
  {
    id: "artifact",
    title: "Verify Artifact",
    icon: Package,
    description:
      "Check that a file is exactly the artifact that was signed: hash, PQC / classical / hybrid signature, certificate and timestamp.",
    bullets: [
      "Firmware, binaries, packages, archives, model files",
      "Includes Artifact Integrity Analysis (what changed)",
    ],
    route: ARTIFACT_UPLOAD_ROUTE,
  },
];

export default function ChooseVerificationType() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  const handleNext = () => {
    const type = TYPES.find((t) => t.id === selected);
    if (!type) {
      alert("Please choose what you want to verify.");
      return;
    }
    navigate(type.route);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <p style={{ fontSize: "24px", fontWeight: 700, color: "#1F2937" }} className="mb-1">
        What do you want to verify?
      </p>
      <p className="text-sm text-gray-400 mb-5">
        Documents and artifacts are checked differently. Pick the one that matches your file.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
        {TYPES.map((type) => {
          const Icon = type.icon;
          const isSelected = selected === type.id;
          return (
            <div
              key={type.id}
              onClick={() => setSelected(type.id)}
              onDoubleClick={() => navigate(type.route)}
              className={`p-5 rounded-2xl border-2 cursor-pointer transition bg-white ${
                isSelected ? "border-blue-500" : "border-gray-200 hover:border-blue-300"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ background: isSelected ? "#DBEAFE" : "#F1F5F9" }}
                >
                  <Icon size={20} color={isSelected ? "#2563EB" : "#64748B"} />
                </div>
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    isSelected ? "border-blue-600 bg-blue-600" : "border-gray-300"
                  }`}
                >
                  {isSelected && <Check size={12} className="text-white" />}
                </div>
              </div>
              <p className="text-[16px] font-semibold text-gray-800 mb-1">{type.title}</p>
              <p className="text-[13px] text-gray-400 mb-3 leading-relaxed">{type.description}</p>
              <ul className="space-y-1">
                {type.bullets.map((b) => (
                  <li key={b} className="text-[12.5px] text-gray-500 flex items-start gap-1.5">
                    <span className="text-blue-500 mt-0.5">•</span> {b}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={handleNext}
          className="text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition rounded-[10px]"
          style={{ height: "44px", padding: "0 24px" }}
        >
          Next
        </button>
      </div>
    </div>
  );
}